import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { authAPI } from '@/services/api';
import { APP_CONFIG, STORAGE_KEYS } from '@/config/app';
import { t } from '@/i18n';

const safeJsonParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const asNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const refreshBonusState = async () => {
  try {
    const { useBonusStore } = await import('@/stores/bonus');
    await useBonusStore().fetchBonuses({ force: true });
  } catch {
    // Bonus state must not block auth.
  }
};

const resetBonusState = async () => {
  try {
    const { useBonusStore } = await import('@/stores/bonus');
    useBonusStore().reset();
  } catch {
    // Bonus state must not block logout.
  }
};

const normalizePlayer = (payload = {}) => {
  const profile = payload.profile && typeof payload.profile === 'object' ? payload.profile : {};
  const email = profile?.m?.d || null;
  const username = String(payload.username || '').trim();
  const displayName = String(profile?.n || username || '').trim();

  return {
    id: payload.userid || null,
    username,
    name: displayName,
    email,
    profile,
    balance: asNumber(payload.balance, 0),
    bonus: asNumber(payload.bonus, 0),
    currency: String(payload.currency || 'ARS').trim() || 'ARS',
    status: 'active',
  };
};

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem(STORAGE_KEYS.token) || '');
  const player = ref(safeJsonParse(localStorage.getItem(STORAGE_KEYS.player)));
  const loading = ref(false);
  const error = ref(null);

  const isAuthenticated = computed(() => Boolean(token.value));
  const playerName = computed(() => {
    if (!player.value) return '';
    if (player.value.name) return player.value.name;
    if (player.value.username) return player.value.username;
    return '';
  });

  const persistSession = ({ accessToken, playerData }) => {
    token.value = accessToken;
    player.value = playerData;
    localStorage.setItem(STORAGE_KEYS.token, accessToken);
    localStorage.setItem(STORAGE_KEYS.player, JSON.stringify(playerData));
  };

  const clearSession = () => {
    token.value = '';
    player.value = null;
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.player);
  };

  const setError = (message) => {
    error.value = message || t('errors.auth.requestFailed');
  };

  const clearError = () => {
    error.value = null;
  };

  const refreshSession = async () => {
    const response = await authAPI.session();
    if (!response.success) {
      return response;
    }

    const currentToken = token.value;
    const normalizedPlayer = normalizePlayer(response.data);
    persistSession({
      accessToken: currentToken,
      playerData: normalizedPlayer,
    });

    return { success: true, data: normalizedPlayer };
  };

  const refreshBalance = async () => {
    if (!token.value) {
      return { success: false, error: t('errors.auth.sessionNotFound') };
    }

    const response = await authAPI.balance();
    if (!response.success) {
      setError(response.error.message);
      return { success: false, error: response.error.message };
    }

    const nextPlayer = {
      ...(player.value || {}),
      balance: asNumber(response.data.balance, 0),
      bonus: asNumber(response.data.bonus, 0),
      currency: String(response.data.currency || player.value?.currency || 'ARS'),
    };

    persistSession({
      accessToken: token.value,
      playerData: nextPlayer,
    });

    return { success: true, data: nextPlayer };
  };

  const login = async ({ username, password, operator = APP_CONFIG.operator }) => {
    loading.value = true;
    clearError();

    try {
      const response = await authAPI.login({ username, password, operator });
      if (!response.success) {
        setError(response.error.message);
        return {
          success: false,
          error: response.error.message,
          codeString: response.error.codeString,
          code: response.error.code,
          status: response.error.status,
        };
      }

      if (response.data?.otp_required) {
        return {
          success: true,
          otpRequired: true,
          otpToken: response.data.otp_token,
          expiresIn: response.data.expires_in,
          data: response.data,
        };
      }

      const accessToken = String(response.data.access_token || '').trim();
      if (!accessToken) {
        setError(t('errors.auth.loginNoToken'));
        return { success: false, error: error.value };
      }

      const normalizedPlayer = normalizePlayer(response.data);
      persistSession({
        accessToken,
        playerData: normalizedPlayer,
      });

      const sessionResult = await refreshSession();
      if (!sessionResult.success) {
        clearSession();
        setError(sessionResult.error?.message || t('errors.auth.sessionValidation'));
        return { success: false, error: error.value };
      }

      await refreshBonusState();
      return { success: true, data: player.value };
    } catch (err) {
      setError(err?.message || t('errors.auth.login'));
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  };

  const verifyOtp = async ({ otpToken, code, operator = APP_CONFIG.operator }) => {
    loading.value = true;
    clearError();

    try {
      const response = await authAPI.verifyOtp({ otpToken, code, operator });
      if (!response.success) {
        setError(response.error.message);
        return {
          success: false,
          error: response.error.message,
          codeString: response.error.codeString,
          code: response.error.code,
          status: response.error.status,
        };
      }

      const accessToken = String(response.data.access_token || '').trim();
      if (!accessToken) {
        setError(t('errors.auth.verifyNoToken'));
        return { success: false, error: error.value };
      }

      const normalizedPlayer = normalizePlayer(response.data);
      persistSession({
        accessToken,
        playerData: normalizedPlayer,
      });

      const sessionResult = await refreshSession();
      if (!sessionResult.success) {
        clearSession();
        setError(sessionResult.error?.message || t('errors.auth.sessionValidation'));
        return { success: false, error: error.value };
      }

      await refreshBonusState();
      return { success: true, data: player.value };
    } catch (err) {
      setError(err?.message || t('errors.auth.verify'));
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  };
  const register = async ({ username, password, email, name, country = 'AR', language = 'es', operator = APP_CONFIG.operator }) => {
    loading.value = true;
    clearError();

    try {
      const response = await authAPI.register({
        username,
        password,
        email,
        name,
        country,
        language,
        operator,
      });

      if (!response.success) {
        setError(response.error.message);
        return {
          success: false,
          error: response.error.message,
          codeString: response.error.codeString,
          code: response.error.code,
          status: response.error.status,
        };
      }

      if (response.data?.otp_required) {
        return {
          success: true,
          otpRequired: true,
          otpToken: response.data.otp_token,
          expiresIn: response.data.expires_in,
          data: response.data,
        };
      }

      const accessToken = String(response.data.access_token || '').trim();
      if (!accessToken) {
        return { success: true, data: response.data };
      }

      const normalizedPlayer = normalizePlayer(response.data);
      persistSession({
        accessToken,
        playerData: normalizedPlayer,
      });

      await refreshSession();
      await refreshBonusState();
      return { success: true, data: player.value };
    } catch (err) {
      setError(err?.message || t('errors.auth.register'));
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  };

  const logout = async () => {
    loading.value = true;

    try {
      if (token.value) {
        await authAPI.logout();
      }
    } finally {
      clearSession();
      clearError();
      loading.value = false;
      const { useProfileStore } = await import('@/stores/profile');
      const { useTransactionsStore } = await import('@/stores/transactions');
      useProfileStore().reset();
      useTransactionsStore().reset();
      await resetBonusState();
    }
  };

  const restoreSession = async () => {
    const storedToken = localStorage.getItem(STORAGE_KEYS.token);
    if (!storedToken) {
      clearSession();
      return { success: false, error: t('errors.auth.noSavedSession') };
    }

    token.value = storedToken;
    const sessionResult = await refreshSession();
    if (!sessionResult.success) {
      clearSession();
      return { success: false, error: sessionResult.error?.message || t('session.expiredTitle') };
    }

    await refreshBonusState();
    return { success: true, data: player.value };
  };

  const requestPasswordRecovery = async ({ email, operator = APP_CONFIG.operator }) => {
    loading.value = true;
    clearError();

    try {
      const response = await authAPI.requestPasswordRecovery({ email, operator });
      if (!response.success) {
        setError(response.error.message);
        return { success: false, error: response.error.message };
      }

      return { success: true, data: response.data };
    } catch (err) {
      setError(err?.message || t('errors.auth.recovery'));
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  };

  const resetPassword = async ({ token: resetToken, password, operator = APP_CONFIG.operator }) => {
    loading.value = true;
    clearError();

    try {
      const response = await authAPI.resetPassword({ token: resetToken, password, operator });
      if (!response.success) {
        setError(response.error.message);
        return { success: false, error: response.error.message };
      }

      return { success: true, data: response.data };
    } catch (err) {
      setError(err?.message || t('errors.auth.reset'));
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  };

  const updateProfile = async (profileData) => {
    const current = player.value || {};
    const profile = {
      ...(current.profile || {}),
      ...(profileData.profile || {}),
      ...(profileData.name ? { n: profileData.name } : {}),
    };

    const updated = {
      ...current,
      ...(profileData.username ? { username: profileData.username } : {}),
      ...(profileData.name ? { name: profileData.name } : {}),
      ...(profileData.email ? { email: profileData.email } : {}),
      profile,
    };

    persistSession({
      accessToken: token.value,
      playerData: updated,
    });

    return { success: true, data: updated };
  };

  return {
    player,
    token,
    loading,
    error,
    isAuthenticated,
    playerName,
    login,
    verifyOtp,
    register,
    logout,
    restoreSession,
    refreshSession,
    refreshBalance,
    requestPasswordRecovery,
    resetPassword,
    updateProfile,
    clearError,
  };
});
